import { useEffect, useCallback } from 'react';
import { type Comment } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useCommentsData } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useEditorAnnotations } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus.ts';
import { CONTENT_STATUS_ARCHIVED } from '@confluence/archive-pages/entry-points/archivingConstants.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { containsNetworkError } from '@confluence/network';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import type { UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment } from './__types__/UnreadCommentCountUnreadCommentQuery.ts';
import { useUnreadComments } from './hooks/useUnreadComments.ts';
import { useViewedComments } from './hooks/useViewedComments.ts';
import { useUnreadCommentsObserverSetup } from './hooks/useUnreadCommentsObserverSetup.ts';
import {
	useIsDanglingCallback,
	useDanglingAndResolvedHandler,
} from './hooks/useDanglingAndResolvedHandler.ts';
import { createUnreadCommentsSet } from './utils/unreadCommentsUtils.ts';

const MAX_UNREAD_COMMENTS_SUPPORTED = 1000;

export interface PageUnreadCommentsProps {
	pageMode: 'view' | 'edit';
}

export interface UseUnreadCommentsSetupProps {
	pageId?: string;
	pageContentTypeProps?: PageUnreadCommentsProps;
	/**
	 * Callback to notify external systems about the comment being read (e.g. content types, whiteboards, databases)
	 * @param commentIds - The IDs of the comments that have been read
	 */
	onCommentRead?: (commentIds: string[]) => void;
}

export const useUnreadCommentsSetup = ({
	pageId,
	pageContentTypeProps,
	onCommentRead,
}: UseUnreadCommentsSetupProps): void => {
	const [
		{ isUpdatingUnreadComments },
		{
			setIsUpdatingUnreadComments,
			setIsUnreadQueryLoading,
			updateUnreadCommentIDSet,
			setShouldClearVisibleThreads,
		},
	] = useUnreadComments();

	const { userId } = useSessionData();

	// Setup observer for unread comments
	useUnreadCommentsObserverSetup({ pageId, onCommentRead });

	// Destructure pageContentTypeProps with defaults
	const { pageMode } = pageContentTypeProps || {};

	const { contentStatus } = useContentStatus();

	// Get processed comments data
	const { commentsDataMap, error, openComments, isDataProcessed } = useProcessedCommentsDataState();
	const loading = !isDataProcessed;

	// Get comments data directly from comments-data package (for pages only)
	// This fixes the circular dependency issue
	const [{ orderedActiveAnnotationIdList = [] }] = useCommentsData();

	const { isDanglingCallback } = useIsDanglingCallback({ pageMode });

	// We still need these for the logic in handleUpdateUnreadComments
	const [editorAnnotationsState] = useEditorAnnotations();
	const { annotations, pageId: editorPageId } = editorAnnotationsState;

	const isRenderer = pageMode === 'view';
	const rendererActionsResult = useRendererActions();
	const { rendererActions, pageId: rendererPageId } = rendererActionsResult || {};

	const { getViewedCommentsFresh } = useViewedComments({
		pageId: pageId || '',
	});

	useEffect(() => {
		// isUnreadQueryLoading is used in other areas of the code to make sure unread logic is processed before further logic
		setIsUnreadQueryLoading(loading);
	}, [loading, setIsUnreadQueryLoading]);

	// Handle GraphQL query errors with proper error handling
	useEffect(() => {
		if (!error || !pageId) {
			return;
		}

		// Handle expected errors (403/unauthorized, network errors) - just mark as handled
		if (isUnauthorizedError(error) || containsNetworkError(error)) {
			markErrorAsHandled(error);
			return;
		}

		// Handle unexpected errors (including 500) - track them with context
		setContentErrorAttributes(error, {
			objectId: pageId,
			objectType: 'page',
		});

		getMonitoringClient().submitError(error, {
			attribution: Attribution.COMMENTS,
		});

		markErrorAsHandled(error);
	}, [error, pageId]);

	useEffect(() => {
		if (pageId) {
			updateUnreadCommentIDSet(new Set(), 'removeAll');
			setShouldClearVisibleThreads(false);
			// reset so we can recreate the set for the page that the user is changing to
			setIsUpdatingUnreadComments(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageId]);

	const resetStates = useCallback(() => {
		setIsUpdatingUnreadComments(false);
	}, [setIsUpdatingUnreadComments]);

	const handleUpdateUnreadComments = useCallback(async () => {
		if (userId && isUpdatingUnreadComments) {
			if (loading) {
				return [];
			}

			if (!pageId) {
				return;
			}

			const currentAllCommentsFromQuery: (Comment | null)[] = openComments || [];

			if (currentAllCommentsFromQuery.length === 0) {
				resetStates(); // we no longer need to create the set if there are no comments on the page
				return;
			}

			// Skip if renderer actions or editor annotations are for different page
			if (
				pageMode &&
				((isRenderer && rendererActions && rendererPageId !== pageId) ||
					(!isRenderer && annotations && editorPageId !== pageId))
			) {
				return; // Wait for next rerender when rendererActions / annotations are updated for the current page
			}

			const rendererAnnotations = rendererActions?.getAnnotationMarks() || [];
			const annotationsInEditorDoc = new Set<string>(annotations || []);

			if (currentAllCommentsFromQuery.length < MAX_UNREAD_COMMENTS_SUPPORTED) {
				const isArchived = contentStatus === CONTENT_STATUS_ARCHIVED;

				const hasPageComments = currentAllCommentsFromQuery.some(
					(
						comment:
							| UnreadCommentCountUnreadCommentQuery_comments_PaginatedCommentList_nodes_Comment
							| Comment
							| null,
					) => comment?.location?.type === 'FOOTER',
				);

				// skip creating unread comments set if the page is archived
				// or if there are no page comments and inline comments
				if (
					isArchived ||
					(pageMode &&
						!hasPageComments &&
						((isRenderer && rendererAnnotations?.length === 0) ||
							(!isRenderer && annotationsInEditorDoc?.size === 0)))
				) {
					return;
				}

				// continuing here means one of these 3 scenarios are true so we do want to check for unread comments
				// 1. no inline comments, yes page comments
				// 2. yes inline comments, no page comments
				// 3. yes inline comments, yes page comments

				const viewedComments = await getViewedCommentsFresh();

				if (viewedComments.success && viewedComments.data) {
					void createUnreadCommentsSet({
						allComments: currentAllCommentsFromQuery,
						currentUserId: userId,
						pageId,
						updateUnreadCommentIDSet,
						resetStates,
						viewedComments: viewedComments.data,
						isDanglingCallback,
					});
				}
			}
		}
	}, [
		userId,
		isUpdatingUnreadComments,
		loading,
		pageId,
		pageMode,
		isRenderer,
		rendererActions,
		rendererPageId,
		annotations,
		editorPageId,
		openComments,
		resetStates,
		contentStatus,
		getViewedCommentsFresh,
		updateUnreadCommentIDSet,
		isDanglingCallback,
	]);

	useDanglingAndResolvedHandler({
		orderedActiveAnnotationIdList,
		commentsDataMap,
		userId: userId || '',
		pageId: pageId || '',
		isDanglingCallback: pageMode ? isDanglingCallback : undefined, // use dangling callback if content type is a page
	});

	useEffect(() => {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (process.env.REACT_SSR) {
			return;
		}

		void handleUpdateUnreadComments();
	}, [handleUpdateUnreadComments, rendererActions, annotations]);
};
